import React, {
    memo,
    MouseEvent,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { PlayerStartStage } from '@yandex-int/ims.stream-player-public-types/build-es5/PlayerStartStage';
import { VideoPlayingState } from '@yandex-int/ims.stream-player-public-types/build-es5/VideoPlayingState';
import noop from 'lodash/noop';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useVideoPlayer } from '../../utils/VideoPlayerProvider';
import { withInvariant } from '../../utils/withInvariant';
import { CircularProgress } from '../feedback/CircularProgress';
import { LinearProgress } from '../feedback/LinearProgress';
import { IconMute } from '../icon/IconMute';
import { IconPlay } from '../icon/IconPlay';
import { IconUnmute } from '../icon/IconUnmute';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';

const VideoContainer = styled.div.withConfig({ displayName: 'VideoContainer' })`
    display: block;
    width: 100%;
    height: 100%;
    -webkit-user-drag: none;
    user-drag: none;
    user-select: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
`;

interface VideoProps {
    title?: string;
    firstFrameUrl?: string;
    streams?: Inspire.Stream[];
    firstFrameHash?: string;
    uuid?: string;
    onToggleMuteUnmute?: (mute: boolean) => void;
    onVideoEnd?: VoidFunction;
    onVideoStart?: VoidFunction;
    onVideoPlay?: VoidFunction;
    onVideoPause?: VoidFunction;
    soundContainer?: HTMLDivElement | null;
}

export const ContentPlayer = withInvariant(
    memo((props: VideoProps) => {
        const {
            title,
            firstFrameUrl,
            streams,
            firstFrameHash,
            uuid,
            onToggleMuteUnmute,
            onVideoEnd,
            onVideoStart,
            onVideoPlay,
            onVideoPause,
            soundContainer,
        } = props;
        const isVisibleRef = useRef<boolean>(false);
        const containerRef = useRef<HTMLDivElement | null>(null);
        const player = useVideoPlayer();
        const playerState = player?.playerState;

        const [progress, setProgress] = useState<number>(0);

        useLayoutEffect(() => {
            if (!containerRef.current || !player) {
                return noop;
            }

            const unmount = player.mount({
                uuid: uuid,
                first_frame_url: firstFrameUrl,
                first_frame_Hash: firstFrameHash,
                container: containerRef.current,
                streams: streams?.map((stream) => ({
                    stream: stream.stream,
                    stream_type: stream.streamType,
                })),
                onToggleMuteUnmute,
                onVideoEnd,
                onVideoStart,
                onVideoPlay,
                onVideoPause,
            });

            return () => unmount?.();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [containerRef.current, player?.mount]);

        useLayoutEffect(() => {
            // Можем подписываться на запуск и остановку плеера только когда понимаем, что он доступен
            if (!containerRef.current || !player || !player.isPlayerEnabled) {
                return noop;
            }

            const offIntersection = player.onIntersection(containerRef.current, (entry) => {
                const isVisible = Boolean(entry && entry.intersectionRatio >= 0.5);
                if (isVisible !== isVisibleRef.current && containerRef.current) {
                    isVisibleRef.current = isVisible;

                    if (isVisible) {
                        player.play(containerRef.current);
                    } else {
                        player.pause(containerRef.current);
                    }
                }
            });

            const offProgessChange = player.onProgessChange(setProgress);

            return () => {
                offIntersection();
                offProgessChange();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [containerRef.current, player]);

        const onPlayPauseClick = useCallback(
            (event: MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                if (containerRef.current && containerRef.current === player?.getActiveNode()) {
                    const playState = playerState?.playingState;
                    playState === VideoPlayingState.PAUSE
                        ? player?.play(containerRef.current)
                        : player?.pause(containerRef.current);

                    return;
                }

                if (containerRef.current) {
                    player?.play(containerRef.current);
                }
            },
            [player, playerState, containerRef],
        );

        const onVolumeControlClick = useCallback(
            (event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                const wasMuted = playerState?.muted;
                player?.setMuted(!wasMuted);
            },
            [player, playerState],
        );

        const soundControl = useMemo(
            () => (
                <Box>
                    <IconButton size="s" onClick={onVolumeControlClick}>
                        {playerState?.muted ? (
                            <IconMute color="#FFF" size={30} />
                        ) : (
                            <IconUnmute color="#FFF" size={30} />
                        )}
                    </IconButton>
                </Box>
            ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [player, playerState],
        );

        const circularProgressEnabled =
            (playerState?.startStage !== PlayerStartStage.started ||
                playerState?.playingState === VideoPlayingState.BUFFERING) &&
            !playerState?.isShortVideo &&
            containerRef.current === player?.getActiveNode();

        const playControlEnabled =
            playerState?.playingState === VideoPlayingState.PAUSE ||
            containerRef.current !== player?.getActiveNode();

        return (
            <Box position="relative" width="100%" height="100%">
                <VideoContainer
                    ref={containerRef}
                    onClick={onPlayPauseClick}
                    title={title}
                    aria-label={title}
                />

                {playControlEnabled ? (
                    <Box
                        onClick={onPlayPauseClick}
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        zIndex={1}
                    >
                        <IconButton size="l" variant="filled" borderRadius="50%">
                            <IconPlay />
                        </IconButton>
                    </Box>
                ) : null}

                {circularProgressEnabled ? (
                    <Box
                        onClick={onPlayPauseClick}
                        position="absolute"
                        top="50%"
                        left="50%"
                        zIndex={10}
                        transform="translate(-50%, -50%)"
                    >
                        <CircularProgress size={32} color="#FFFFFF" />
                    </Box>
                ) : null}

                {soundContainer ? createPortal(soundControl, soundContainer) : soundControl}

                {!playerState?.isShortVideo && (
                    <Box position="absolute" width="100%" bottom={0} id="timeline">
                        <LinearProgress progress={progress} />
                    </Box>
                )}
            </Box>
        );
    }),
    () => null,
);
